import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ManifesteService } from 'src/app/core/services/manifeste.service';
import { ZoneService } from 'src/app/core/services/zone.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-liste-manifestes',
  templateUrl: './liste-manifestes.component.html',
  styleUrls: ['./liste-manifestes.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ListeManifestesComponent implements OnInit, OnDestroy {

  manifestes: any[] = [];
  manifestesFiltres: any[] = [];
  indicateurChargement = true;
  pageCourante = 1;
  taillePage = 20;
  totalElements = 0;

  recherche = '';
  statutFiltre = '';
  zones: any[] = [];

  // ── Création : étape 1 (infos) ──────────────────────────────────────────────
  afficherModalCreation = false;
  creationEnCours = false;
  etapeCreation: 1 | 2 = 1;

  formulaireCreation: any = {
    numeroReference: '',
    zoneDepartId: null,
    zoneArriveeId: null,
    immatriculationVehicule: '',
    poidsTotal: null,
    descriptionContenu: '',
    observations: ''
  };

  // ── Création : étape 2 (commandes) ─────────────────────────────────────────
  commandesEligibles: any[] = [];
  commandesEligiblesFiltrees: any[] = [];
  commandesSelectionnees: Set<number> = new Set();
  chargementCommandes = false;
  rechercheCommande = '';

  // Filtres étape 2
  filtreStatutCmd = '';
  filtreClientId: number | '' = '';
  filtreDateDebut = '';
  filtreDateFin = '';
  filtreMontantMin: number | null = null;
  filtreMontantMax: number | null = null;
  filtreTri = 'date_desc';
  filtresOuverts = false;

  // Clients chargés depuis l'API
  clients: any[] = [];

  // Recherche live par numéro (quand liste éligible vide)
  commandesRecherchees: any[] = [];
  rechercheEnCours = false;
  private rechercheSubject = new Subject<string>();
  private rechercheSubscription?: Subscription;

  // ── Détail commandes d'un manifeste ────────────────────────────────────────
  manifesteSelectionne: any = null;
  commandesManifeste: any[] = [];
  afficherDetailCommandes = false;
  chargementDetail = false;

  readonly STATUTS = [
    { valeur: '', libelle: 'Tous les statuts' },
    { valeur: 'EN_PREPARATION', libelle: 'En préparation' },
    { valeur: 'CHARGE',         libelle: 'Chargé' },
    { valeur: 'EXPEDIE',        libelle: 'Expédié' },
    { valeur: 'EN_TRANSIT',     libelle: 'En transit' },
    { valeur: 'ARRIVE',         libelle: 'Arrivé' },
    { valeur: 'RECEPTIONNE',    libelle: 'Réceptionné' },
    { valeur: 'ANNULE',         libelle: 'Annulé' },
    { valeur: 'TERMINE',        libelle: 'Terminé' },
  ];

  // ── Réconciliation ───────────────────────────────────────────────────────────
  afficherDialogReconciliation = false;
  reconciliationEnCours = false;
  lignesReconciliation: {
    commandeId: number; numeroCommande: string;
    etat: 'RECU_OK' | 'MANQUANT' | 'ENDOMMAGE'; observations: string;
  }[] = [];
  observationsReconciliation = '';

  // ── Sheet détail commande (dans la réconciliation) ──────────────────────────
  commandeEnDetail: any = null;
  chargementDetailSheet = false;

  ouvrirSheetCommande(commandeId: number): void {
    if (this.commandeEnDetail?.id === commandeId) { this.commandeEnDetail = null; return; }
    this.chargementDetailSheet = true;
    this.commandeEnDetail = null;
    this.http.get<any>(`${environment.apiurl}commandes/${commandeId}`).subscribe({
      next: data => { this.commandeEnDetail = data; this.chargementDetailSheet = false; },
      error: () => { this.chargementDetailSheet = false; this.toast.erreur('Impossible de charger le détail'); }
    });
  }

  fermerSheetCommande(): void { this.commandeEnDetail = null; }

  // ── Affectation livreurs (last-mile) ────────────────────────────────────────
  afficherDialogAffectation = false;
  affectationEnCours = false;
  lignesAffectation: { commandeId: number; numeroCommande: string; livreurId: number | null }[] = [];
  livreurs: any[] = [];
  chargementLivreurs = false;

  constructor(
    private manifService: ManifesteService,
    private zoneService: ZoneService,
    private router: Router,
    private toast: ToastService,
    private authService: AuthService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.charger();
    this.chargerZones();
    this.chargerClients();
    this.rechercheSubscription = this.rechercheSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(terme => {
        if (!terme || terme.length < 3) { this.rechercheEnCours = false; return of([]); }
        this.rechercheEnCours = true;
        return this.manifService.rechercherCommandeParNumero(terme).pipe(catchError(() => of([])));
      })
    ).subscribe(cmds => {
      this.commandesRecherchees = Array.isArray(cmds) ? cmds : [];
      this.rechercheEnCours = false;
    });
  }

  ngOnDestroy(): void {
    this.rechercheSubscription?.unsubscribe();
  }

  charger(): void {
    this.indicateurChargement = true;
    this.manifService.listerManifestes(this.pageCourante - 1, this.taillePage).subscribe({
      next: (data: any) => {
        this.manifestes = data?.content ?? [];
        this.totalElements = data?.totalElements ?? this.manifestes.length;
        this.appliquerFiltres();
        this.indicateurChargement = false;
      },
      error: () => { this.indicateurChargement = false; }
    });
  }

  private chargerZones(): void {
    this.zoneService.obtenirToutesLesZones().subscribe({
      next: (data: any[]) => { this.zones = Array.isArray(data) ? data : []; },
      error: () => { this.zones = []; }
    });
  }

  appliquerFiltres(): void {
    let r = [...this.manifestes];
    if (this.recherche.trim()) {
      const q = this.recherche.toLowerCase();
      r = r.filter(m =>
        (m.numeroReference ?? '').toLowerCase().includes(q) ||
        (m.zoneDepartNom ?? '').toLowerCase().includes(q) ||
        (m.zoneArriveeNom ?? '').toLowerCase().includes(q)
      );
    }
    if (this.statutFiltre) {
      const aliases: Record<string, string[]> = { EN_PREPARATION: ['EN_PREPARATION', 'ENREGISTRE'] };
      const valeurs = aliases[this.statutFiltre] ?? [this.statutFiltre];
      r = r.filter(m => valeurs.includes(this.statutManifeste(m)));
    }
    this.manifestesFiltres = r;
  }

  badgeStatut(s: string): string {
    const map: Record<string, string> = {
      EN_PREPARATION: 'badge--gris', ENREGISTRE: 'badge--gris',
      CHARGE: 'badge--orange',
      EXPEDIE: 'badge--bleu', EN_TRANSIT: 'badge--bleu',
      ARRIVE: 'badge--violet',
      RECEPTIONNE: 'badge--vert',
      ANNULE: 'badge--rouge', TERMINE: 'badge--vert-fonce',
    };
    return map[s] ?? 'badge--gris';
  }

  libelleStatut(s: string): string {
    return this.STATUTS.find(x => x.valeur === s)?.libelle ?? s;
  }

  /** Actions de workflow disponibles selon le statut actuel */
  actionsWorkflow(m: any): { label: string; statut: string; cls: string }[] {
    const st = this.statutManifeste(m);
    const actions: { label: string; statut: string; cls: string }[] = [];
    switch (st) {
      case 'EN_PREPARATION':
      case 'ENREGISTRE':
        actions.push({ label: 'Marquer chargé', statut: 'CHARGE', cls: 'btn-wf-orange' });
        break;
      case 'CHARGE':
        actions.push({ label: 'Expédier', statut: 'EXPEDIE', cls: 'btn-wf-bleu' });
        break;
      case 'EXPEDIE':
        actions.push({ label: 'En transit', statut: 'EN_TRANSIT', cls: 'btn-wf-bleu' });
        break;
      case 'EN_TRANSIT':
        actions.push({ label: 'Marquer arrivé', statut: 'ARRIVE', cls: 'btn-wf-violet' });
        break;
    }
    return actions;
  }

  changerStatut(m: any, statut: string): void {
    this.manifService.changerStatut(m.id, statut).subscribe({
      next: () => { this.toast.succes('Statut mis à jour'); this.charger(); },
      error: () => this.toast.erreur('Erreur lors du changement de statut')
    });
  }

  // ── Ouverture / fermeture modal création ────────────────────────────────────

  nouveau(): void {
    if (!this.peutCreerManifeste()) {
      this.toast.erreur('Vous n\'avez pas les droits pour créer un manifeste');
      return;
    }
    this.etapeCreation = 1;
    this.commandesSelectionnees.clear();
    this.commandesEligibles = [];
    this.commandesEligiblesFiltrees = [];
    this.rechercheCommande = '';
    this.formulaireCreation = {
      numeroReference: '',
      zoneDepartId: null,
      zoneArriveeId: null,
      immatriculationVehicule: '',
      poidsTotal: null,
      descriptionContenu: '',
      observations: ''
    };
    this.afficherModalCreation = true;
  }

  fermerModalCreation(): void {
    if (this.creationEnCours) return;
    this.afficherModalCreation = false;
  }

  // ── Étape 1 → 2 ─────────────────────────────────────────────────────────────

  passerEtape2(): void {
    if (!this.formulaireCreation.zoneDepartId || !this.formulaireCreation.zoneArriveeId) {
      this.toast.erreur('Sélectionnez la zone de départ et la zone d\'arrivée');
      return;
    }
    if (Number(this.formulaireCreation.zoneDepartId) === Number(this.formulaireCreation.zoneArriveeId)) {
      this.toast.erreur('Les zones de départ et d\'arrivée doivent être différentes');
      return;
    }
    this.etapeCreation = 2;
    this.chargementCommandes = true;
    this.manifService.obtenirCommandesEligibles(
      Number(this.formulaireCreation.zoneDepartId),
      Number(this.formulaireCreation.zoneArriveeId)
    ).subscribe({
      next: (cmds: any[]) => {
        this.commandesEligibles = Array.isArray(cmds) ? cmds : [];
        this.filtrerCommandesEligibles();
        this.chargementCommandes = false;
      },
      error: () => {
        this.commandesEligibles = [];
        this.commandesEligiblesFiltrees = [];
        this.chargementCommandes = false;
        this.toast.erreur('Impossible de charger les commandes éligibles');
      }
    });
  }

  filtrerCommandesEligibles(): void {
    const q = this.rechercheCommande.toLowerCase().trim();
    let result = [...this.commandesEligibles];

    // Recherche texte
    if (q) {
      result = result.filter(c =>
        (c.numeroCommande ?? '').toLowerCase().includes(q) ||
        (c.numeroSuivi ?? '').toLowerCase().includes(q) ||
        (c.expediteur?.nom ?? c.expediteurNom ?? '').toLowerCase().includes(q) ||
        (c.destinataire?.nom ?? c.destinataireNom ?? '').toLowerCase().includes(q)
      );
      if (result.length === 0) this.rechercheSubject.next(this.rechercheCommande.trim());
    } else {
      this.commandesRecherchees = [];
    }

    // Filtre statut
    if (this.filtreStatutCmd) {
      result = result.filter(c => (c.statut ?? '') === this.filtreStatutCmd);
    }

    // Filtre client (par id utilisateur expéditeur ou destinataire)
    if (this.filtreClientId !== '') {
      const id = Number(this.filtreClientId);
      result = result.filter(c =>
        c.expediteur?.id === id || c.expediteurId === id ||
        c.destinataire?.id === id || c.destinataireId === id ||
        c.clientId === id
      );
    }

    // Filtre date début
    if (this.filtreDateDebut) {
      const debut = new Date(this.filtreDateDebut);
      result = result.filter(c => c.dateCreation && new Date(c.dateCreation) >= debut);
    }

    // Filtre date fin
    if (this.filtreDateFin) {
      const fin = new Date(this.filtreDateFin);
      fin.setHours(23, 59, 59);
      result = result.filter(c => c.dateCreation && new Date(c.dateCreation) <= fin);
    }

    // Filtre montant
    if (this.filtreMontantMin != null) {
      result = result.filter(c => (c.montantTotal ?? 0) >= this.filtreMontantMin!);
    }
    if (this.filtreMontantMax != null) {
      result = result.filter(c => (c.montantTotal ?? 0) <= this.filtreMontantMax!);
    }

    // Tri
    result = this.trierCommandes(result);

    this.commandesEligiblesFiltrees = result;
  }

  private trierCommandes(list: any[]): any[] {
    return [...list].sort((a, b) => {
      switch (this.filtreTri) {
        case 'date_asc':  return new Date(a.dateCreation ?? 0).getTime() - new Date(b.dateCreation ?? 0).getTime();
        case 'date_desc': return new Date(b.dateCreation ?? 0).getTime() - new Date(a.dateCreation ?? 0).getTime();
        case 'montant_asc':  return (a.montantTotal ?? 0) - (b.montantTotal ?? 0);
        case 'montant_desc': return (b.montantTotal ?? 0) - (a.montantTotal ?? 0);
        case 'expediteur': return (a.expediteur?.nom ?? a.expediteurNom ?? '').localeCompare(b.expediteur?.nom ?? b.expediteurNom ?? '');
        default: return 0;
      }
    });
  }

  chargerClients(): void {
    this.http.get<any[]>(`${environment.apiurl}users/role/USER`)
      .subscribe({ next: data => this.clients = (data || []).sort((a, b) => (a.nom ?? '').localeCompare(b.nom ?? '')), error: () => {} });
  }

  nomClient(c: any): string {
    const prenom = c.prenom ?? '';
    const nom = c.nom ?? c.username ?? '';
    return [prenom, nom].filter(Boolean).join(' ');
  }

  get statutsDisponibles(): string[] {
    const statuts = new Set<string>();
    this.commandesEligibles.forEach(c => { if (c.statut) statuts.add(c.statut); });
    return Array.from(statuts);
  }

  get nbFiltresActifs(): number {
    let n = 0;
    if (this.filtreStatutCmd) n++;
    if (this.filtreClientId !== '') n++;
    if (this.filtreDateDebut) n++;
    if (this.filtreDateFin) n++;
    if (this.filtreMontantMin != null) n++;
    if (this.filtreMontantMax != null) n++;
    return n;
  }

  reinitialiserFiltresCmd(): void {
    this.filtreStatutCmd = '';
    this.filtreClientId = '';
    this.filtreDateDebut = '';
    this.filtreDateFin = '';
    this.filtreMontantMin = null;
    this.filtreMontantMax = null;
    this.filtreTri = 'date_desc';
    this.filtrerCommandesEligibles();
  }

  get nomClientSelectionne(): string {
    if (this.filtreClientId === '') return '';
    const c = this.clients.find(c => c.id === Number(this.filtreClientId));
    return c ? this.nomClient(c) : String(this.filtreClientId);
  }

  /** Commandes résultant de la recherche globale, en excluant celles déjà dans la liste éligible. */
  get commandesRechercheesUniques(): any[] {
    const idsEligibles = new Set(this.commandesEligibles.map(c => c.id));
    return this.commandesRecherchees.filter(c => !idsEligibles.has(c.id));
  }

  toggleCommande(id: number): void {
    if (this.commandesSelectionnees.has(id)) {
      this.commandesSelectionnees.delete(id);
    } else {
      this.commandesSelectionnees.add(id);
    }
  }

  toutSelectionner(): void {
    this.commandesEligiblesFiltrees.forEach(c => this.commandesSelectionnees.add(c.id));
  }

  toutDeselectionner(): void {
    this.commandesSelectionnees.clear();
  }

  retourEtape1(): void {
    this.etapeCreation = 1;
    this.commandesRecherchees = [];
    this.rechercheCommande = '';
    this.reinitialiserFiltresCmd();
    this.filtresOuverts = false;
  }

  // ── Soumission finale ────────────────────────────────────────────────────────

  soumettreCreation(): void {
    const payload: any = {
      numeroReference: (this.formulaireCreation.numeroReference || '').trim() || null,
      zoneDepartId: Number(this.formulaireCreation.zoneDepartId),
      zoneArriveeId: Number(this.formulaireCreation.zoneArriveeId),
      immatriculationVehicule: (this.formulaireCreation.immatriculationVehicule || '').trim() || null,
      poidsTotal: this.formulaireCreation.poidsTotal ? Number(this.formulaireCreation.poidsTotal) : null,
      descriptionContenu: (this.formulaireCreation.descriptionContenu || '').trim() || null,
      observations: (this.formulaireCreation.observations || '').trim() || null,
      commandeIds: Array.from(this.commandesSelectionnees),
      statut: 'EN_PREPARATION'
    };

    this.creationEnCours = true;
    this.manifService.creerManifeste(payload).subscribe({
      next: () => {
        this.creationEnCours = false;
        this.afficherModalCreation = false;
        this.toast.succes(`Manifeste créé avec ${this.commandesSelectionnees.size} commande(s)`);
        this.charger();
      },
      error: () => {
        this.creationEnCours = false;
        this.toast.erreur('Erreur lors de la création du manifeste');
      }
    });
  }

  // ── Détail commandes d'un manifeste ─────────────────────────────────────────

  voirDetail(m: any): void {
    this.voirCommandes(m);
  }

  voirCommandes(m: any): void {
    this.manifesteSelectionne = m;
    this.afficherDetailCommandes = true;
    this.chargementDetail = true;
    this.manifService.obtenirCommandesManifeste(m.id).subscribe({
      next: (cmds: any[]) => {
        this.commandesManifeste = Array.isArray(cmds) ? cmds : [];
        this.chargementDetail = false;
      },
      error: () => {
        this.commandesManifeste = [];
        this.chargementDetail = false;
      }
    });
  }

  fermerDetailCommandes(): void {
    this.afficherDetailCommandes = false;
    this.manifesteSelectionne = null;
    this.commandesManifeste = [];
  }

  retirerCommandeManifeste(commandeId: number): void {
    if (!this.manifesteSelectionne) return;
    this.manifService.retirerCommande(this.manifesteSelectionne.id, commandeId).subscribe({
      next: () => {
        this.commandesManifeste = this.commandesManifeste.filter(c => c.id !== commandeId);
        this.toast.succes('Commande retirée du manifeste');
        this.charger();
      },
      error: () => this.toast.erreur('Erreur lors du retrait')
    });
  }

  peutCreerManifeste(): boolean {
    const role = (this.authService.obtenirUtilisateurCourant()?.role || '').toString().toUpperCase();
    return role === 'ADMIN' || role === 'MANAGER' || role === 'RESPONSABLE_ZONE' || role === 'GESTIONNAIRE_ZONE';
  }

  peutSupprimerManifeste(): boolean {
    const role = (this.authService.obtenirUtilisateurCourant()?.role || '').toString().toUpperCase();
    return role === 'ADMIN';
  }

  supprimer(m: any): void {
    const ref = m.numeroReference ? `"${m.numeroReference}"` : `#${m.id}`;
    if (!confirm(`Supprimer le manifeste ${ref} ?\nLes commandes liées seront détachées.`)) return;
    this.manifService.supprimerManifeste(m.id).subscribe({
      next: () => {
        this.toast.succes('Manifeste supprimé');
        this.charger();
      },
      error: () => this.toast.erreur('Erreur lors de la suppression du manifeste')
    });
  }

  nomZone(id: number | null): string {
    if (!id) return '—';
    const z = this.zones.find(x => x.id === Number(id));
    return z ? z.nom : '—';
  }

  voirDetailCommande(commandeId: number): void {
    this.fermerDetailCommandes();
    this.router.navigate(['/commande', commandeId]);
  }

  badgeStatutCommande(statut: string): string {
    const map: Record<string, string> = {
      CREE: 'badge--gris', EN_ATTENTE: 'badge--gris', EN_PREPARATION: 'badge--gris',
      AU_DEPOT: 'badge--orange', EN_TRI: 'badge--orange',
      RECUPERE: 'badge--bleu', EN_LIVRAISON: 'badge--bleu', EN_TRANSIT: 'badge--bleu',
      RECEPTIONNE: 'badge--vert', LIVRE: 'badge--vert', LIVRE_PARTIEL: 'badge--vert',
      ANNULE: 'badge--rouge', INCIDENT: 'badge--rouge',
    };
    return map[statut] ?? 'badge--gris';
  }

  libelleStatutCommande(statut: string): string {
    const map: Record<string, string> = {
      CREE: 'Créé', EN_ATTENTE: 'En attente', EN_PREPARATION: 'En préparation',
      AU_DEPOT: 'Au dépôt', EN_TRI: 'En tri',
      RECUPERE: 'Récupéré', EN_LIVRAISON: 'En livraison', EN_TRANSIT: 'En transit',
      RECEPTIONNE: 'Réceptionné', LIVRE: 'Livré', LIVRE_PARTIEL: 'Livré partiel',
      ANNULE: 'Annulé', INCIDENT: 'Incident',
    };
    return map[statut] ?? statut ?? '—';
  }

  formaterHistorique(h: string): string {
    // Retirer la partie ISO date de la phrase pour ne garder que le texte
    return h.replace(/\s+le\s+\d{4}-\d{2}-\d{2}T[\d:.]+$/, '').trim();
  }

  extraireDate(h: string): string {
    const match = h.match(/le\s+(\d{4}-\d{2}-\d{2}T[\d:.]+)$/);
    if (!match) return '';
    try {
      const d = new Date(match[1]);
      return d.toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
    } catch { return ''; }
  }

  changerPage(p: number): void {
    this.pageCourante = p;
    this.charger();
  }

  // ── Réconciliation ───────────────────────────────────────────────────────────

  ouvrirReconciliation(m: any): void {
    this.manifesteSelectionne = m;
    this.observationsReconciliation = '';
    this.chargementDetail = true;
    this.afficherDialogReconciliation = true;
    this.manifService.obtenirCommandesManifeste(m.id).subscribe({
      next: (cmds: any[]) => {
        this.lignesReconciliation = (cmds || []).map(c => ({
          commandeId: c.id,
          numeroCommande: c.numeroCommande ?? ('#' + c.id),
          etat: 'RECU_OK' as const,
          observations: ''
        }));
        this.chargementDetail = false;
      },
      error: () => { this.chargementDetail = false; }
    });
  }

  fermerReconciliation(): void {
    if (this.reconciliationEnCours) return;
    this.afficherDialogReconciliation = false;
    this.lignesReconciliation = [];
    this.manifesteSelectionne = null;
    this.commandeEnDetail = null;
  }

  soumettreReconciliation(): void {
    if (!this.manifesteSelectionne) return;
    this.reconciliationEnCours = true;
    this.manifService.reconcilier(
      this.manifesteSelectionne.id,
      this.lignesReconciliation,
      this.observationsReconciliation
    ).subscribe({
      next: () => {
        this.reconciliationEnCours = false;
        this.afficherDialogReconciliation = false;
        this.toast.succes('Réconciliation enregistrée — manifeste réceptionné');
        this.charger();
      },
      error: () => {
        this.reconciliationEnCours = false;
        this.toast.erreur('Erreur lors de la réconciliation');
      }
    });
  }

  nbRecuOk(): number   { return this.lignesReconciliation.filter(l => l.etat === 'RECU_OK').length; }
  nbManquant(): number { return this.lignesReconciliation.filter(l => l.etat === 'MANQUANT').length; }
  nbEndommage(): number { return this.lignesReconciliation.filter(l => l.etat === 'ENDOMMAGE').length; }

  // ── Affectation livreurs (last-mile) ────────────────────────────────────────

  ouvrirAffectation(m: any): void {
    this.manifesteSelectionne = m;
    this.afficherDialogAffectation = true;
    this.chargementDetail = true;
    this.chargementLivreurs = true;

    // Charger les commandes réceptionnées (RECU_OK) du manifeste
    this.manifService.obtenirCommandesManifeste(m.id).subscribe({
      next: (cmds: any[]) => {
        this.lignesAffectation = (cmds || [])
          .filter(c => ['RECEPTIONNE', 'RECU_OK'].includes(c.statut ?? c.status ?? ''))
          .map(c => ({
            commandeId: c.id,
            numeroCommande: c.numeroCommande ?? ('#' + c.id),
            livreurId: c.livreur?.id ?? null
          }));
        this.chargementDetail = false;
      },
      error: () => { this.chargementDetail = false; }
    });

    // Charger les livreurs de la zone d'arrivée
    const zoneArriveeId = m.zoneArriveeId;
    if (zoneArriveeId) {
      this.manifService.livreursParZone(zoneArriveeId).subscribe({
        next: (data: any[]) => { this.livreurs = data || []; this.chargementLivreurs = false; },
        error: () => {
          this.livreurs = [];
          this.chargementLivreurs = false;
          // Fallback : charger tous les livreurs depuis /users/role/LIVREUR
          this.http.get<any[]>(`${environment.apiurl}users/role/LIVREUR`).subscribe({
            next: (data: any[]) => this.livreurs = data || [],
            error: () => {}
          });
        }
      });
    } else {
      this.chargementLivreurs = false;
    }
  }

  fermerAffectation(): void {
    if (this.affectationEnCours) return;
    this.afficherDialogAffectation = false;
    this.lignesAffectation = [];
    this.manifesteSelectionne = null;
  }

  soumettreAffectation(): void {
    if (!this.manifesteSelectionne) return;
    const affectations = this.lignesAffectation
      .filter(l => l.livreurId != null)
      .map(l => ({ commandeId: l.commandeId, livreurId: l.livreurId! }));

    if (affectations.length === 0) {
      this.toast.erreur('Assignez au moins un livreur');
      return;
    }
    this.affectationEnCours = true;
    this.manifService.affecterLivraisons(this.manifesteSelectionne.id, affectations).subscribe({
      next: () => {
        this.affectationEnCours = false;
        this.afficherDialogAffectation = false;
        this.toast.succes(`${affectations.length} commande(s) assignée(s) aux livreurs`);
        this.charger();
      },
      error: () => {
        this.affectationEnCours = false;
        this.toast.erreur('Erreur lors de l\'affectation');
      }
    });
  }

  get nbAffectationsAssignees(): number {
    return this.lignesAffectation.filter(l => l.livreurId != null).length;
  }

  nomLivreur(id: number | null): string {
    if (!id) return '—';
    const l = this.livreurs.find(x => x.id === id);
    return l ? `${l.prenom ?? ''} ${l.nom ?? ''}`.trim() : '—';
  }

  statutManifeste(m: any): string {
    return m.status ?? m.statut ?? '';
  }

  compterParStatut(statut: string): number {
    // ENREGISTRE est l'alias legacy de EN_PREPARATION
    const aliases: Record<string, string[]> = { EN_PREPARATION: ['EN_PREPARATION', 'ENREGISTRE'] };
    const valeurs = aliases[statut] ?? [statut];
    return this.manifestes.filter(m => valeurs.includes(this.statutManifeste(m))).length;
  }

  get totalPages(): number { return Math.ceil(this.totalElements / this.taillePage); }
  get pages(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }
  readonly Math = Math;
}
